import { useCallback, useMemo } from "react";
import { omitUndefined, toastManager } from "@salestrak/ui";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { useCustomFieldValueCommit } from "#app/hooks/issue-view/use-custom-field-value-commit";
import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { useOpenIssueTypeMigration } from "#app/lib/issue-type-migration-dialog-context";
import type { IssueRow } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useOptionPickerOps } from "#app/lib/hooks/use-custom-field-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { AnalyticsView } from "#app/lib/analytics";
import type { IssueCardActions } from "./issue-card-context";

type UseIssueCardEditActionsOptions = {
  changeStage?: (issue: IssueRow, stageId: string) => void;
  onAddressChanged?: () => void;
  onIssueChanged?: () => void;
  /** The screen that hosts the card, for the `issue.edit` event. */
  view?: AnalyticsView;
};

export function useIssueCardEditActions({
  changeStage,
  onAddressChanged,
  onIssueChanged,
  view,
}: UseIssueCardEditActionsOptions = {}): IssueCardActions {
  const { teamMap, stageMap } = useWorkspaceData();
  const tracking = useMemo(() => ({ via: "card", ...(view ? { view } : {}) }), [view]);
  const { updateAddress, updateTeams, updateStage } =
    useIssueViewMutations(tracking);
  const openTypeMigration = useOpenIssueTypeMigration();
  const { onCreateOption, optionActions } = useOptionPickerOps();
  // The one custom-field write path (set = single-key merge, `undefined` =
  // clear), shared with the drawer and the table's cells.
  const commitFieldValue = useCustomFieldValueCommit({
    onChanged: onIssueChanged,
    tracking,
  });

  const handleAddressSelect = useCallback(
    (issue: IssueRow, wholeAddress: string) => {
      updateAddress({
        variables: { issueId: issue.issueId, address: { wholeAddress } },
        optimisticResponse: {
          updateIssue: omitUndefined({
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: issue.addressId,
            address: {
              __typename: "Address" as const,
              id: issue.address?.id ?? `optimistic-${issue.issueId}`,
              addressId:
                issue.address?.addressId ?? `optimistic-${issue.issueId}`,
              wholeAddress,
            },
          }),
        },
        onCompleted: (data) => {
          const result = data.updateIssue;
          if (result.__typename === "Issue") {
            onAddressChanged?.();
            onIssueChanged?.();
          } else {
            toastManager.add({
              title:
                "message" in result
                  ? result.message
                  : "Failed to update address",
              type: "error",
            });
          }
        },
        onError: (error) =>
          toastMutationError(error, "Failed to update address"),
      });
    },
    [onAddressChanged, onIssueChanged, updateAddress],
  );

  const handleAddressClear = useCallback(
    (issue: IssueRow) => {
      updateAddress({
        variables: { issueId: issue.issueId, address: null },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: null,
            address: null,
          },
        },
        onCompleted: (data) => {
          const result = data.updateIssue;
          if (result.__typename === "Issue") {
            onAddressChanged?.();
            onIssueChanged?.();
          } else {
            toastManager.add({
              title:
                "message" in result ? result.message : "Failed to clear address",
              type: "error",
            });
          }
        },
        onError: (error) => toastMutationError(error, "Failed to clear address"),
      });
    },
    [onAddressChanged, onIssueChanged, updateAddress],
  );

  const handleStageChange = useCallback(
    (issue: IssueRow, stageId: string) => {
      if (stageId === issue.stageId) return;
      if (changeStage) {
        changeStage(issue, stageId);
        return;
      }
      const stage = stageMap.get(stageId);
      updateStage({
        variables: { issueId: issue.issueId, stageId },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            stageId,
            stage: {
              __typename: "Stage" as const,
              id: stage?.id ?? stageId,
              stageId,
              name: stage?.name ?? "",
              sortOrder: stage?.sortOrder ?? 0,
              type: stage?.type ?? null,
              isDefault: stage?.isDefault ?? false,
              workflowId: issue.workflowId,
              icon: {
                __typename: "Icon" as const,
                icon: stage?.icon?.icon ?? null,
                color: stage?.icon?.color ?? null,
                percentage: null,
              },
              updateCycle: stage?.updateCycle ?? null,
              updateCycleOff: stage?.updateCycleOff ?? false,
            },
          },
        },
        onCompleted: (data) => {
          const result = data.updateIssue;
          if (result.__typename === "Issue") {
            onIssueChanged?.();
          } else {
            toastManager.add({
              title:
                "message" in result
                  ? result.message
                  : "Failed to update stage",
              type: "error",
            });
          }
        },
        onError: (error) =>
          toastMutationError(error, "Failed to update stage"),
      });
    },
    [changeStage, onIssueChanged, stageMap, updateStage],
  );

  const handleProjectTypeChange = useCallback(
    (issue: IssueRow, issueTypeId: string) => {
      if (issueTypeId === issue.issueTypeId) return;
      openTypeMigration({ issueId: issue.issueId, issueTypeId, onMigrated: onIssueChanged, via: "card" });
    },
    [onIssueChanged, openTypeMigration],
  );

  const handleTeamsChange = useCallback(
    (issue: IssueRow, teamIds: string[]) => {
      updateTeams({
        variables: { issueId: issue.issueId, teamIds },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            teams: teamIds.map((teamId) => {
              const team = teamMap.get(teamId);
              const kept = issue.teams?.find((item) => item.teamId === teamId);
              return {
                __typename: "Team" as const,
                id: teamId,
                teamId,
                teamName: team?.teamName ?? kept?.teamName ?? "?",
                icon: {
                  __typename: "Icon" as const,
                  icon: team?.icon?.icon ?? kept?.icon?.icon ?? null,
                  color: team?.icon?.color ?? kept?.icon?.color ?? null,
                  percentage: null,
                },
              };
            }),
          },
        },
        onCompleted: (data) => {
          if (data.updateIssue.__typename === "Issue") onIssueChanged?.();
        },
        onError: (error) =>
          toastMutationError(error, "Failed to update teams"),
      });
    },
    [onIssueChanged, teamMap, updateTeams],
  );

  const handleFieldValuesChange = useCallback(
    (issue: IssueRow, field: CustomFieldDef, value: unknown) =>
      commitFieldValue(issue, field.customFieldId, value),
    [commitFieldValue],
  );

  return useMemo(
    () => ({
      onAddressSelect: handleAddressSelect,
      onAddressClear: handleAddressClear,
      onFieldValuesChange: handleFieldValuesChange,
      onStageChange: handleStageChange,
      onProjectTypeChange: handleProjectTypeChange,
      onTeamsChange: handleTeamsChange,
      onCreateOption,
      optionActions,
    }),
    [
      handleAddressSelect,
      handleAddressClear,
      handleFieldValuesChange,
      handleStageChange,
      handleProjectTypeChange,
      handleTeamsChange,
      onCreateOption,
      optionActions,
    ],
  );
}
